// One job as a line in a drawer's list or a picker (finance-real, the user 2026-10-23: "same entities should use same
// components"): its name, one subline (jobSubline: [client · ] period [ · how it bills]), its amount, and a trailing slot
// beside it for what the list adds, the shared status pill, Take off, or a figure. The invoice drawer's jobs, the quote
// drawer's and form's, New invoice's and Add jobs to bill's picks, a team invoice's claims, New team invoice's ticks and
// Claim another job's picker all are this line. Its name and subline wrap on a phone, never cut, and the amount stays in
// view on the right (DD-055). Pressed open (`onOpen`), it is a button; ticked (`check`), a label around its checkbox;
// otherwise text. A child that only displays its props: no hook (TD-107).
import type { ReactNode } from "react";
import { MoneyText } from "@/components/ui/MoneyText";
import { cn } from "@/utils/cn";

interface Props {
  name: string;
  /** jobSubline's: "Mister Coffee · October 2026 · Recurring". */
  sub: string;
  amountMinor?: number | null;
  /** A muted line under the subline: a held claim's lock, a job's list price ("List 800,00 € · 15% off"). */
  note?: string | null;
  /** Inside the line, before the amount: a figure the list adds ("Free to claim: 120,00 €"). */
  aside?: ReactNode;
  /** Outside the line's own button or label, after it: a status pill or dropdown, Take off. */
  trailing?: ReactNode;
  /** The line's checkbox: the whole line is its label. */
  check?: { checked: boolean; onChange: () => void; disabled?: boolean };
  /** The line is a button: it opens the job, or the claim in place. */
  onOpen?: () => void;
  /** A picker's chosen line. */
  pressed?: boolean;
  ariaLabel?: string;
  /** Where focus goes back once a form opened from it closes. */
  focusKey?: string;
  /** Greyed: a line shown for why, not to act on (New team invoice's claims not owed). */
  muted?: boolean;
}

const LINE = "min-w-0 flex-1 flex items-center gap-3 py-2 text-left text-sm";

export function JobLine({ name, sub, amountMinor, note, aside, trailing, check, onOpen, pressed, ariaLabel, focusKey, muted = false }: Props) {
  const body = (
    <>
      {check && <input type="checkbox" checked={check.checked} disabled={check.disabled} onChange={check.onChange} />}
      <span className="min-w-0 flex-1">
        <span className={cn("block [overflow-wrap:anywhere]", muted ? "text-gray-500" : "text-gray-900")}>{name}</span>
        {sub && <span className="block text-xs text-gray-500 [overflow-wrap:anywhere]">{sub}</span>}
        {note && <span className="block text-xs text-gray-500">{note}</span>}
      </span>
      {aside}
      {amountMinor !== undefined && <MoneyText amountMinor={amountMinor} className={cn("shrink-0 text-sm", muted && "text-gray-500")} />}
    </>
  );
  const line = check ? (
    <label className={LINE}>{body}</label>
  ) : onOpen ? (
    <button type="button" onClick={onOpen} aria-pressed={pressed} aria-label={ariaLabel} data-claim-focus={focusKey} className={cn(LINE, "px-1 -mx-1 rounded-md hover:bg-gray-50", pressed && "bg-blue-50")}>
      {body}
    </button>
  ) : (
    <div className={LINE}>{body}</div>
  );
  return trailing ? (
    <div className="flex items-center gap-2">
      {line}
      <span className="flex shrink-0 items-center gap-2">{trailing}</span>
    </div>
  ) : (
    line
  );
}
